import Phaser from 'phaser';
import { getSettings, setSetting, type Settings, hasDeviceOrientation } from '../services/settings';
import { resumeAudio, sfx } from '../services/audio';
import { addNeonBackdrop } from '../util/glow';
import { createButton } from '../util/button';
import { drawSpeaker, drawNote, drawShake, drawPhone } from '../util/glyphs';
import { activePalette, setActivePalette, css, lighten, PALETTES } from '../util/palette';
import { submitFeedback, MAX_FEEDBACK_LEN } from '../services/feedback';
import { deleteAccountData } from '../services/account';

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';
/** Destructive-action accent (delete account) — no palette carries a red. */
const DANGER = 0xe25555;

/** Shows the palette picker card — re-enabled with the Picnic light theme. */
const SHOW_THEME_PICKER = true;

/** Draws a row's leading glyph centered at (x, y) in the accent color. */
type Glyph = (g: Phaser.GameObjects.Graphics, x: number, y: number, color: number, s?: number) => void;

interface ToggleRow {
  key: keyof Settings;
  label: string;
  description?: string;
  glyph: Glyph;
  /** Hide unless this predicate is true. */
  visibleWhen?: () => boolean;
}

const ROWS: ToggleRow[] = [
  { key: 'sfx', label: 'SOUND EFFECTS', glyph: drawSpeaker },
  { key: 'music', label: 'MUSIC', glyph: drawNote },
  { key: 'shake', label: 'SCREEN SHAKE', description: 'Off by default for steadier play', glyph: drawShake },
  {
    key: 'tiltDeviceMode',
    label: 'DEVICE TILT',
    description: 'Tilt to steer; off = joystick',
    glyph: drawPhone,
    visibleWhen: () => hasDeviceOrientation(),
  },
];

// Card geometry, shared across every settings row so the column reads as a set.
// Base (phone) values; scaled up on tablets in build().
const CARD_W = 322;
const ROW_H = 66;
const CARD_GAP = 12;

/** CSS width >= 700 px is treated as tablet (mirrors MainMenuScene). */
const TABLET_MIN = 700;

interface SettingsData {
  returnTo?: string;
}

export class SettingsScene extends Phaser.Scene {
  static readonly KEY = 'SettingsScene';
  private returnTo: string = 'MainMenuScene';
  /** Tablet scale factor for cards/fonts; 1.0 on phones. Set in build(). */
  private s = 1;
  private cardW = CARD_W;

  constructor() {
    super({ key: SettingsScene.KEY });
  }

  create(data?: SettingsData): void {
    this.returnTo = data?.returnTo ?? 'MainMenuScene';
    this.build();
  }

  private goBack(): void {
    if (this.returnTo === 'GameScene') {
      // GameScene was paused when we launched, so resume it instead of
      // start()ing — start() would tear down the in-progress run.
      this.scene.stop();
      this.scene.resume('GameScene');
    } else {
      this.scene.start(this.returnTo);
    }
  }

  private build(): void {
    this.children.removeAll();
    const { width, height } = this.scale;
    const cx = width / 2;
    const pal = activePalette();

    // Tablet: scale the whole settings column up to use the extra room.
    // Height-guarded like the main menu so the column never outgrows short
    // desktop windows.
    this.s = width >= TABLET_MIN
      ? Math.max(1.0, Math.min(1.0 + (width - TABLET_MIN) / 280, height / 780, 1.6))
      : 1.0;
    const s = this.s;
    this.cardW = Math.round(CARD_W * s);

    // Neon-candy backdrop — opaque, covers the paused GameScene when launched
    // in-game. No bokeh: build() re-runs on every toggle, which would stack
    // drifting tweens. (Shared helper; see util/glow.ts.)
    addNeonBackdrop(this, { glowY: height * 0.26, bokeh: false });

    // Brand wordmark + title.
    this.add
      .text(cx, Math.round(42 * s), 'SHMERGE', {
        fontFamily: FONT, fontSize: `${Math.round(13 * s)}px`, fontStyle: 'bold', color: css(pal.primary), resolution: DPR,
      })
      .setOrigin(0.5)
      .setAlpha(0.7);
    this.add
      .text(cx, Math.round(76 * s), 'Settings', {
        fontFamily: FONT, fontSize: `${Math.round(32 * s)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0.5);
    // A short accent underline so the title has a deliberate anchor.
    const ul = this.add.graphics();
    ul.fillStyle(pal.primary, 0.9);
    ul.fillRoundedRect(cx - Math.round(22 * s), Math.round(98 * s), Math.round(44 * s), 3, 1.5);

    let y = Math.round(128 * s);
    const rowH = Math.round(ROW_H * s);
    const cardX = cx - this.cardW / 2;
    for (const row of ROWS) {
      if (row.visibleWhen && !row.visibleWhen()) continue;
      const rowCenterY = y + rowH / 2;

      this.drawCard(cardX, y, this.cardW, rowH);
      this.buildRow(cardX, rowCenterY, row);

      y += rowH + Math.round(CARD_GAP * s);
    }

    // Live theme picker — its own section card (disabled until redesign).
    if (SHOW_THEME_PICKER) {
      this.buildPalettePicker(cx, cardX, y + Math.round(10 * s));
    }

    let bottomInset = 0;
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--sab').trim();
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0) bottomInset = n;
    } catch {}
    const backBtnY = height - Math.round(56 * s) - bottomInset;

    createButton(this, {
      x: cx, y: backBtnY, width: Math.round(200 * s), height: Math.round(56 * s), label: 'BACK', fontSize: Math.round(18 * s),
      onClick: () => {
        resumeAudio();
        this.goBack();
      },
    });

    // Privacy policy link — sits above the BACK button with generous spacing.
    // Font bumped to 14px and interactive area padded to 44px tall for easier tapping.
    const privacyY = backBtnY - Math.round(58 * s);
    const privacyText = this.add
      .text(cx, privacyY, 'Privacy Policy', {
        fontFamily: FONT, fontSize: `${Math.round(14 * s)}px`, color: css(pal.textFaint), resolution: DPR,
      })
      .setOrigin(0.5);
    // Use a generous hit rectangle (220 × 44) so small-fingered users can tap easily.
    privacyText.setInteractive(
      new Phaser.Geom.Rectangle(-110, -22, 220, 44),
      Phaser.Geom.Rectangle.Contains,
    );
    privacyText.input!.cursor = 'pointer';
    privacyText.on('pointerover', () => privacyText.setColor(css(pal.textSoft)));
    privacyText.on('pointerout', () => privacyText.setColor(css(pal.textFaint)));
    privacyText.on('pointerup', () => {
      if (typeof window !== 'undefined') window.open('https://superslop.net/privacy.html', '_blank');
    });

    // Feedback + account-deletion pair, anchored to the bottom chrome with the
    // privacy link so the toggle column above never has to make room for them.
    const actionsY = privacyY - Math.round(52 * s);
    const actionW = Math.round((this.cardW - 12 * s) / 2);
    const actionGap = Math.round(12 * s);
    createButton(this, {
      x: cx - (actionW + actionGap) / 2, y: actionsY, width: actionW, height: Math.round(44 * s),
      label: 'SEND FEEDBACK', fontSize: Math.round(13 * s), variant: 'secondary',
      onClick: () => {
        resumeAudio();
        this.showFeedbackForm();
      },
    });
    createButton(this, {
      x: cx + (actionW + actionGap) / 2, y: actionsY, width: actionW, height: Math.round(44 * s),
      label: 'DELETE ACCOUNT', fontSize: Math.round(13 * s), variant: 'secondary',
      color: DANGER, textColor: css(DANGER),
      onClick: () => {
        resumeAudio();
        this.showDeleteConfirm();
      },
    });
  }

  /**
   * DOM overlay with a textarea (same approach as the GameOverScene rename
   * input — Phaser has no native text entry). Posts to the /feedback API and
   * reports success/failure inline.
   */
  private showFeedbackForm(): void {
    const pal = activePalette();

    const backdrop = document.createElement('div');
    Object.assign(backdrop.style, {
      position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.55)',
      zIndex: '9998', display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      width: 'min(340px, calc(100vw - 48px))', padding: '20px',
      background: css(pal.surfaceDeep), borderRadius: '16px',
      border: `2px solid ${css(pal.surfaceBorder)}`, fontFamily: FONT,
      display: 'flex', flexDirection: 'column', gap: '12px',
    });

    const title = document.createElement('div');
    title.textContent = 'Send Feedback';
    Object.assign(title.style, { fontSize: '18px', fontWeight: '700', color: css(pal.text), textAlign: 'center' });

    const input = document.createElement('textarea');
    input.maxLength = MAX_FEEDBACK_LEN;
    input.rows = 5;
    input.placeholder = 'Bugs, ideas, anything — what should we improve?';
    Object.assign(input.style, {
      fontSize: '15px', padding: '10px 12px', resize: 'none',
      border: `2px solid ${css(pal.button)}`, borderRadius: '10px',
      background: css(pal.surface), color: css(pal.text),
      outline: 'none', fontFamily: FONT,
    });

    const status = document.createElement('div');
    Object.assign(status.style, { fontSize: '12px', color: css(pal.textMuted), textAlign: 'center', minHeight: '15px' });

    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: '10px' });
    const makeBtn = (label: string, primary: boolean) => {
      const b = document.createElement('button');
      b.textContent = label;
      Object.assign(b.style, {
        flex: '1', padding: '11px 0', fontSize: '14px', fontWeight: '700',
        fontFamily: FONT, borderRadius: '10px', cursor: 'pointer',
        border: primary ? 'none' : `2px solid ${css(pal.surfaceBorder)}`,
        background: primary ? css(pal.button) : 'transparent',
        color: primary ? '#ffffff' : css(pal.textSoft),
      });
      return b;
    };
    const cancelBtn = makeBtn('CANCEL', false);
    const sendBtn = makeBtn('SEND', true);
    row.append(cancelBtn, sendBtn);

    panel.append(title, input, status, row);
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);
    input.focus();

    const close = () => {
      if (document.body.contains(backdrop)) document.body.removeChild(backdrop);
    };
    this.events.once('shutdown', close);
    backdrop.addEventListener('pointerdown', (e) => {
      if (e.target === backdrop) close();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
    cancelBtn.addEventListener('click', close);

    sendBtn.addEventListener('click', () => {
      const message = input.value.trim();
      if (!message) {
        status.textContent = 'Write a little something first 🙂';
        return;
      }
      sendBtn.disabled = true;
      cancelBtn.disabled = true;
      sendBtn.style.opacity = '0.6';
      status.textContent = 'Sending…';
      void submitFeedback(message)
        .then(() => {
          status.textContent = 'Thanks — feedback sent! 💛';
          setTimeout(close, 900);
        })
        .catch(() => {
          sendBtn.disabled = false;
          cancelBtn.disabled = false;
          sendBtn.style.opacity = '1';
          status.textContent = "Couldn't send — check your connection and try again.";
        });
    });
  }

  /**
   * In-canvas confirmation for account deletion (App Store Guideline
   * 5.1.1(v)). On confirm: best-effort server-side score deletion, full local
   * wipe, then a page reload so every module-level cache restarts fresh.
   */
  private showDeleteConfirm(): void {
    const pal = activePalette();
    const s = this.s;
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    const overlay = this.add.container(0, 0).setDepth(300);

    // Dim + swallow input behind the dialog.
    const dim = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.55)
      .setOrigin(0)
      .setInteractive();
    overlay.add(dim);

    const cardW = this.cardW;
    const cardH = Math.round(238 * s);
    const cardX = cx - cardW / 2;
    const cardY = cy - cardH / 2;
    const g = this.add.graphics();
    g.fillStyle(pal.chipShadow, 0.4);
    g.fillRoundedRect(cardX, cardY + 4, cardW, cardH, 16);
    g.fillStyle(pal.surfaceDeep, 1);
    g.fillRoundedRect(cardX, cardY, cardW, cardH, 16);
    g.lineStyle(1.5, DANGER, 0.7);
    g.strokeRoundedRect(cardX, cardY, cardW, cardH, 16);
    overlay.add(g);

    overlay.add(
      this.add
        .text(cx, cardY + Math.round(34 * s), 'Delete account?', {
          fontFamily: FONT, fontSize: `${Math.round(20 * s)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
        })
        .setOrigin(0.5),
    );
    overlay.add(
      this.add
        .text(
          cx,
          cardY + Math.round(94 * s),
          'This removes your scores from the global leaderboard and erases all local data — name, coins, settings, and high score. This can’t be undone.',
          {
            fontFamily: FONT, fontSize: `${Math.round(13 * s)}px`, color: css(pal.textSoft), resolution: DPR,
            align: 'center', wordWrap: { width: cardW - Math.round(44 * s) },
          },
        )
        .setOrigin(0.5),
    );

    const status = this.add
      .text(cx, cardY + cardH - Math.round(28 * s), '', {
        fontFamily: FONT, fontSize: `${Math.round(12 * s)}px`, color: css(pal.textMuted), resolution: DPR,
        align: 'center', wordWrap: { width: cardW - Math.round(44 * s) },
      })
      .setOrigin(0.5);
    overlay.add(status);

    const btnY = cardY + cardH - Math.round(68 * s);
    const btnW = Math.round((cardW - 56 * s) / 2);
    let busy = false;
    overlay.add(
      createButton(this, {
        x: cx - (btnW + Math.round(12 * s)) / 2, y: btnY, width: btnW, height: Math.round(44 * s),
        label: 'CANCEL', fontSize: Math.round(14 * s), variant: 'secondary',
        onClick: () => {
          if (!busy) overlay.destroy();
        },
      }),
    );
    overlay.add(
      // Secondary (solid-fill) variant: the primary gradient body renders
      // translucent on the Canvas renderer (same fillGradientStyle bug as the
      // old game-over card) and read as a black blob over the dim layer.
      createButton(this, {
        x: cx + (btnW + Math.round(12 * s)) / 2, y: btnY, width: btnW, height: Math.round(44 * s),
        label: 'DELETE', fontSize: Math.round(14 * s), variant: 'secondary',
        color: DANGER, textColor: css(DANGER),
        onClick: () => {
          if (busy) return;
          busy = true;
          status.setText('Deleting…');
          void deleteAccountData().then(({ serverCleared }) => {
            status.setText(
              serverCleared
                ? 'Account deleted.'
                : 'Local data erased. Server unreachable — leaderboard scores may remain.',
            );
            // Reload so settings/palette/audio/wallet module caches all
            // restart from a fresh-install state.
            this.time.delayedCall(serverCleared ? 1100 : 2600, () => window.location.reload());
          });
        },
      }),
    );
  }

  /** A rounded glassy panel with a hairline border + top sheen. The single
   *  surface primitive every settings card is built on, so new rows added later
   *  inherit the look for free. */
  private drawCard(x: number, y: number, w: number, h: number): void {
    const pal = activePalette();
    const g = this.add.graphics().setDepth(-0.5);
    g.fillStyle(pal.chipShadow, 0.4);
    g.fillRoundedRect(x, y + 3, w, h, 14);
    g.fillStyle(pal.surface, 0.9);
    g.fillRoundedRect(x, y, w, h, 14);
    g.lineStyle(1, pal.surfaceBorder, 1);
    g.strokeRoundedRect(x, y, w, h, 14);
    g.fillStyle(0xffffff, 0.04);
    g.fillRoundedRect(x + 5, y + 4, w - 10, h * 0.34, 11);
  }

  private buildPalettePicker(cx: number, cardX: number, y: number): void {
    const pal = activePalette();
    const s = this.s;
    // Single row, same height as the toggle cards so the column reads as one set.
    const cardH = Math.round(ROW_H * s);
    this.drawCard(cardX, y, this.cardW, cardH);
    const rowY = y + cardH / 2;

    // Label + active palette name on the left, mirroring the toggle rows'
    // label/description pair.
    const textX = cardX + Math.round(18 * s);
    this.add
      .text(textX, rowY - Math.round(9 * s), 'THEME', {
        fontFamily: FONT, fontSize: `${Math.round(11 * s)}px`, fontStyle: 'bold', color: css(pal.textLabel), resolution: DPR,
      })
      .setOrigin(0, 0.5);
    this.add
      .text(textX, rowY + Math.round(11 * s), pal.label, {
        fontFamily: FONT, fontSize: `${Math.round(13 * s)}px`, fontStyle: 'bold', color: css(pal.primary), resolution: DPR,
      })
      .setOrigin(0, 0.5);

    const n = PALETTES.length;
    const sh = Math.round(42 * s);
    const gap = Math.round(12 * s);
    // Size swatches to fit however many palettes exist inside the card,
    // leaving ~110px on the left for the THEME label + name.
    const sw = Math.min(Math.round(58 * s), Math.floor((this.cardW - Math.round(128 * s) - (n - 1) * gap) / n));
    const total = n * sw + (n - 1) * gap;
    // Right-aligned, matching the toggles' trailing edge.
    let sx = cardX + this.cardW - Math.round(18 * s) - total + sw / 2;

    for (const p of PALETTES) {
      const active = p.name === pal.name;
      const left = sx - sw / 2;
      const top = rowY - sh / 2;
      // Active swatch gets a soft accent glow behind it.
      if (active) {
        const halo = this.add.graphics();
        halo.fillStyle(p.primary, 0.22);
        halo.fillRoundedRect(left - 4, top - 4, sw + 8, sh + 8, 12);
      }
      const g = this.add.graphics();
      g.fillStyle(p.bgBottom, 1);
      g.fillRoundedRect(left, top, sw, sh, 9);
      g.fillStyle(p.bgTop, 1);
      g.fillRoundedRect(left, top, sw, sh * 0.5, 9);
      // Accent dots: button (top), primary + secondary (bottom).
      g.fillStyle(p.button, 1);
      g.fillCircle(sx, rowY - 8, 4.5);
      g.fillStyle(p.primary, 1);
      g.fillCircle(sx - 11, rowY + 7, 6);
      g.fillStyle(p.secondary, 1);
      g.fillCircle(sx + 9, rowY + 7, 6);
      g.lineStyle(active ? 2.5 : 1, active ? p.primary : pal.surfaceBorder, active ? 1 : 0.8);
      g.strokeRoundedRect(left, top, sw, sh, 9);

      const name = p.name;
      this.add
        .rectangle(sx, rowY, sw, sh, 0xffffff, 0)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          if (name === activePalette().name) return;
          resumeAudio();
          setActivePalette(name);
          this.build();
        });
      sx += sw + gap;
    }
  }

  /** One toggle row rendered inside its card: glyph chip · label/description ·
   *  polished pill toggle. */
  private buildRow(cardX: number, rowCenterY: number, row: ToggleRow): void {
    const pal = activePalette();
    const s = this.s;

    // Leading glyph chip.
    const chipX = cardX + Math.round(36 * s);
    const chipHalf = Math.round(19 * s);
    const chip = this.add.graphics();
    chip.fillStyle(lighten(pal.surface, 0.16), 1);
    chip.fillRoundedRect(chipX - chipHalf, rowCenterY - chipHalf, chipHalf * 2, chipHalf * 2, Math.round(11 * s));
    chip.lineStyle(1, pal.primary, 0.35);
    chip.strokeRoundedRect(chipX - chipHalf, rowCenterY - chipHalf, chipHalf * 2, chipHalf * 2, Math.round(11 * s));
    const gi = this.add.graphics();
    row.glyph(gi, chipX, rowCenterY, pal.primary, 0.74 * s);

    // Label (+ optional description) — vertically centered as a pair.
    const textX = chipX + Math.round(32 * s);
    const hasDesc = !!row.description;
    this.add
      .text(textX, rowCenterY - (hasDesc ? Math.round(9 * s) : 0), row.label, {
        fontFamily: FONT, fontSize: `${Math.round(16 * s)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0, 0.5);
    if (row.description) {
      this.add
        .text(textX, rowCenterY + Math.round(11 * s), row.description, {
          fontFamily: FONT, fontSize: `${Math.round(11 * s)}px`, color: css(pal.textMuted), resolution: DPR,
        })
        .setOrigin(0, 0.5);
    }

    // Polished pill toggle, right-aligned in the card.
    const trackW = Math.round(54 * s);
    const trackH = Math.round(30 * s);
    const trackCx = cardX + this.cardW - Math.round(22 * s) - trackW / 2;
    const trackG = this.add.graphics();
    const knob = this.add
      .circle(trackCx, rowCenterY, Math.round(12 * s), pal.text, 1)
      .setStrokeStyle(1, pal.chipShadow, 0.5);

    const drawTrack = (on: boolean) => {
      trackG.clear();
      const top = on ? lighten(pal.toggleOn, 0.2) : lighten(pal.surface, 0.04);
      const bot = on ? pal.toggleOn : pal.surface;
      trackG.fillGradientStyle(top, top, bot, bot, 1, 1, 1, 1);
      trackG.fillRoundedRect(trackCx - trackW / 2, rowCenterY - trackH / 2, trackW, trackH, trackH / 2);
      trackG.lineStyle(1.5, on ? pal.primary : pal.surfaceBorder, on ? 0.9 : 1);
      trackG.strokeRoundedRect(trackCx - trackW / 2, rowCenterY - trackH / 2, trackW, trackH, trackH / 2);
    };

    const render = (on: boolean, animate: boolean) => {
      drawTrack(on);
      const kx = trackCx + (on ? 1 : -1) * Math.round(12 * s);
      knob.setFillStyle(on ? lighten(pal.primary, 0.12) : pal.text, 1);
      if (animate) {
        this.tweens.add({ targets: knob, x: kx, duration: 170, ease: 'Back.easeOut' });
      } else {
        knob.setX(kx);
      }
    };

    render(getSettings()[row.key], false);

    const hit = this.add
      .rectangle(trackCx, rowCenterY, trackW + 8, trackH + 8, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      const next = !getSettings()[row.key];
      setSetting(row.key, next);
      render(next, true);
      resumeAudio();
      if (row.key === 'sfx' && next) sfx('tickHigh');
    });
  }
}
